import * as React from "react";
import { useTranslation } from "react-i18next";
import { GitFork } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNewTaskStatusMutations } from "../hooks";
import type { Task } from "../types";

interface AdjustmentDialogProps {
  task: Task;
  /** When set, the button is disabled and this string is shown as a tooltip */
  disabledReason?: string;
  /** Controlled open state (e.g. when launched from a "More" menu item). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger button — the dialog is opened externally. */
  hideTrigger?: boolean;
  onSuccess?: () => void;
}

export function AdjustmentDialog({
  task,
  disabledReason,
  open,
  onOpenChange,
  hideTrigger = false,
  onSuccess,
}: AdjustmentDialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = open !== undefined;
  const dialogOpen = isControlled ? open : internalOpen;
  const setDialogOpen = (next: boolean) => {
    if (isControlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };
  const { t } = useTranslation();
  const { adjustMutation } = useNewTaskStatusMutations();

  const handleConfirm = () => {
    adjustMutation.mutate(task.id, {
      onSuccess: () => {
        setDialogOpen(false);
        onSuccess?.();
      },
    });
  };

  const triggerButton = (
    <Button
      size="sm"
      disabled={!!disabledReason}
      className="bg-amber-600 hover:bg-amber-700 text-white"
    >
      <GitFork className="h-4 w-4" />
      {t("adjustment.createAdjustment")}
    </Button>
  );

  if (disabledReason && !hideTrigger) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex">
              {triggerButton}
            </span>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {!hideTrigger && <DialogTrigger asChild>{triggerButton}</DialogTrigger>}

      <DialogContent className="!flex !flex-col sm:max-w-lg p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <GitFork className="h-5 w-5" />
            {t("adjustment.confirmTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t("adjustment.confirmBody")}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("cancel")}
            </Button>
          </DialogClose>
          <Button
            onClick={handleConfirm}
            disabled={adjustMutation.isPending}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {adjustMutation.isPending ? t("saving") : t("adjustment.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
